import { useMemo, useState } from "react";
import { Button, ButtonGroup, Input, ListGroup, ListGroupItem, Modal, ModalBody, ModalHeader } from "reactstrap";
import { msg, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { countries, Country, getPostalCode, State } from "./states";

export interface StateProvincePickerProps {
  value: State | null;
  onChange: (value: State | null) => void;
  id?: string;
}

const StateProvincePicker = ({ value, onChange, id }: StateProvincePickerProps) => {
  const { _ } = useLingui();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState<string>("US");

  const openPicker = () => {
    setSearch("");
    setOpen(true);
  };

  const closePicker = () => setOpen(false);

  const selectState = (s: State) => {
    onChange(s);
    closePicker();
  };

  const activeCountry = countries.find(c => c.postalCode === country) as Country;
  const query = search.trim().toLowerCase();

  const rows = useMemo(() => {
    const matches = activeCountry.states.filter(s => !query || s.toLowerCase().startsWith(query));
    const stateRows: Array<{ kind: "letter"; letter: string } | { kind: "row"; state: State }> = [];

    let lastLetter: string | null = null;
    matches.forEach(s => {
      const letter = s[0].toUpperCase();
      if (letter !== lastLetter) {
        stateRows.push({ kind: "letter", letter });
        lastLetter = letter;
      }
      stateRows.push({ kind: "row", state: s });
    });
    return stateRows;
  }, [activeCountry, query]);

  return (
    <>
      <button
        id={id}
        type="button"
        className="form-select text-start"
        onClick={openPicker}>
        {value
          ? <span className="text-body">{value}</span>
          : <span className="text-muted"><Trans>Select state/province...</Trans></span>}
      </button>

      <Modal
        isOpen={open}
        toggle={closePicker}
        className="my-2"
        size="lg">
        <ModalHeader>
          {_(msg`Select State / Province`)}
          <button
            type="button"
            className="btn-close position-absolute top-0 end-0 m-3 mt-2"
            onClick={closePicker} />
        </ModalHeader>
        <ModalBody className="pt-3">
          <div className="input-group input-group-merge mb-2">
            <span className="input-group-text">
              <i className="bi-search" />
            </span>
            <Input
              type="search"
              className="form-control form-control-lg ps-1 py-0"
              placeholder={_(msg`Search states...`)}
              value={search}
              onChange={e => setSearch(e.target.value)} />
          </div>

          <ButtonGroup className="w-100 mb-3">
            {countries.map(c => (
              <Button
                key={c.postalCode}
                className="px-2"
                color="primary"
                outline={country !== c.postalCode}
                active={country === c.postalCode}
                onClick={() => setCountry(c.postalCode)}>
                {c.name === "United States" && <Trans>United States</Trans>}
                {c.name === "Canada" && <Trans>Canada</Trans>}
                {c.name === "Mexico" && <Trans>Mexico</Trans>}
              </Button>
            ))}
          </ButtonGroup>

          <div className="spp-list vh-70">
            {rows.length === 0
              ? <div className="text-center text-muted py-4">
                <i className="bi-inbox d-block fs-1" />
                <Trans>No matches</Trans>
              </div>
              : <ListGroup flush>
                {rows.map(r =>
                  r.kind === "letter"
                    ? <div
                      key={`l-${r.letter}`}
                      className="list-group-item bg-light fw-semibold small text-uppercase text-primary my-0 py-1">
                      {r.letter}
                    </div>
                    : <ListGroupItem
                      key={r.state}
                      tag="button"
                      action
                      className="d-flex justify-content-between align-items-center py-2"
                      onClick={() => selectState(r.state)}>
                      <span className="fs-5">{r.state}</span>
                      <span aria-hidden="true" className="badge bg-light text-secondary">{getPostalCode(r.state)}</span>
                    </ListGroupItem>
                )}
              </ListGroup>}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default StateProvincePicker;
